import { Component, forwardRef, Input, OnChanges, SimpleChanges, Output, EventEmitter, ElementRef, Renderer2, ViewChild, Injector } from '@angular/core';
import { UntypedFormControl, ControlValueAccessor, NG_VALUE_ACCESSOR, NG_VALIDATORS, NgControl, AbstractControl } from '@angular/forms';
import { NGXLogger } from 'ngx-logger';

import { NgxMaskDirective } from 'ngx-mask';

export function createValidator(country: string | undefined, optional: boolean) {
    return (c: UntypedFormControl) => {
        if (optional && !c.value)
            return null;

        if (!c.value && (country == 'us' || country == 'ca'))
            return { required: true };

        let regex: RegExp;

        if (country == 'us')
            regex = /^(\d{5}(-\d{4})?)?$/;
        else if (country == 'ca')
            regex = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;
        else
            return null;

        const valid = regex.test(c.value);

        return valid ? null : {
            zipValidator: {
                valid,
                country,
                regex,
                value: c.value
            }
        };
    };
}

@Component({
    selector: 'app-zipcode-input',
    template: `
        <input appScrText appZip [country]="country"
            [mask]="mask"
            [attr.id]="inputId"
            [attr.data-testid]="inputId"
            [dropSpecialCharacters]="false"
            [validation]="false"
            [(ngModel)]="value"
            placeholder="Zip/Postal Code"
            [required]="!optional && (country === 'us' || country === 'ca')"
            maxlength="10"
            [disabled]="disabled"
            (blur)="blur()"
        />
    `,
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => ZipCodeComponent),
        multi: true
    }, {
        provide: NG_VALIDATORS,
        useExisting: forwardRef(() => ZipCodeComponent),
        multi: true
    }]
})
export class ZipCodeComponent implements ControlValueAccessor, OnChanges {

    propagateChange: (_: string | undefined) => void = () => {
        // default to empty.
    };

    validateFn: (c: UntypedFormControl) => object | null = () => {
        return null;
    };

    mask = '';

    @Input() ngModel: string | undefined = '';
    @Input() inputId: string | undefined;
    @Input() country: string | undefined = '';
    @Input() disabled = false;
    @Input() optional = false;
    @ViewChild(NgxMaskDirective, { static: false }) ms?: NgxMaskDirective;

    private inOnChanges = false;

    @Output() ngModelChange = new EventEmitter<string>();

    touched = false;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private control?: AbstractControl<any, any> | null;


    constructor(private el: ElementRef, private rend: Renderer2, private log: NGXLogger, private injector: Injector,
    ) {
        this.validateFn = createValidator(this.country, this.optional);
        this.setMask();
    }

    blur() {
        this.touched = true;
        // this.log.trace(`zip: blur reset value to ${this.value}`);
        if (!this.control) {
            const ngControl = this.injector.get(NgControl, null);
            this.control = ngControl?.control;
        }
        this.control?.markAsTouched();
    }

    get value() {
        return this.ngModel;
    }

    set value(val: string | undefined) {
        this.log.trace(`zip setter with ${val} vs previous value ${this.ngModel}`);
        // try allowing undefined but not null.
        if (val === null) {
            return;
        }
        // if we are in the onChanges, setting the value with cause expression changed after check errors.
        // if yes, then set a timeout and do it later.
        if (this.inOnChanges) {
            window.setTimeout(() => {
                this.ngModel = val;// || '';
                this.ngModelChange.emit(val);
                this.propagateChange(val);
            }, 100);
        }
        else {
            this.ngModel = val;// || '';
            this.ngModelChange.emit(val);
            window.setTimeout(() => {
                this.propagateChange(val);
            }, 0);
        }
    }


    ngOnChanges(inputs: SimpleChanges) {

        if (inputs.country) {
            this.country = inputs.country.currentValue;
            this.validateFn = createValidator(this.country, this.optional);
            this.log.trace('country ngOnChange ' + inputs.country.isFirstChange() + ' ' + inputs.country.currentValue);

            //this.log.debug('got inputs ' + JSON.stringify(inputs));
            // let's try this instead.  If the country changed and not the first change.
            if (!inputs.country.isFirstChange() && !inputs.ngModel && inputs.country.previousValue && (inputs.country.currentValue !== inputs.country.previousValue)) {
                window.setTimeout(() => {
                    this.log.trace('resetting zip after country change.  not always showing up was blank value in model however');
                    this.value = '';
                    this.writeValue(undefined);
                    this.ms?.writeValue('');
                }, 0);
            }

            this.inOnChanges = true;
            this.setMask();
            setTimeout(() => {
                this.inOnChanges = false;
            }, 250);
        }
    }

    setMask() {
        if (this.country == 'us')
            this.mask = '00000-0000';
        else if (this.country == 'ca')
            this.mask = 'S0S 0S0';
        else
            this.mask = '';
    }

    writeValue(value: string | undefined) {
        // this.log.trace(`zip: writeValue(${value})`);
        this.value = value;
        if (this.el) {
            // this.log.trace(`zip: call rend set prop (${value}) ${this.rend} ${this.el.nativeElement}`);
            this.rend.setProperty(this.el.nativeElement, 'value', value);
        }
    }

    registerOnChange(fn: (_: string | undefined) => void): void {
        this.propagateChange = fn;
    }

    registerOnTouched() {
        // empty
    }

    validate(c: UntypedFormControl) {
        if (this.touched)
            c.markAsTouched();

        return this.validateFn(c);
    }
}


